<template>
  <div
    class="checkout-result d-flex align-items-center justify-content-center"
  >
    <b-card class="checkout-result-card text-center">
      <b-img
        :src="business.logo"
        :alt="business.name"
        height="60"
        class="mb-2"
      />

      <div
        v-if="show"
        class="py-3"
      >
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem"
          class="mb-3"
        />
        <h4 class="mb-0">
          Procesando su pago...
        </h4>
        <p class="text-muted mb-0">
          {{ messages.reference }} #{{ id }}
        </p>
      </div>

      <div v-else>
        <b-avatar
          :variant="successMessage ? 'light-success' : 'light-danger'"
          size="70"
          class="mb-2"
        >
          <feather-icon
            :icon="successMessage ? 'CheckIcon' : 'XIcon'"
            size="36"
          />
        </b-avatar>

        <h3 class="mt-1 mb-0">
          {{ successMessage || errorMessage }}
        </h3>
        <p class="text-muted mb-0">
          {{ messages.reference }} #{{ id }}
        </p>

        <!-- Comprobante certificado por AZUL. Un solo componente para esta
             pantalla y el historial del perfil: si los campos cambian, cambian
             en los dos lados a la vez. -->
        <payment-receipt
          v-if="receipt && successMessage"
          :receipt="receipt"
          class="mt-2"
        />

        <p
          v-if="receipt && successMessage"
          class="receipt-note no-print"
        >
          También le enviamos una copia por correo electrónico.
        </p>

        <hr class="my-2">

        <!-- Al invitado no se lo redirige: se le dice que ya terminó -->
        <p
          v-if="wasGuest"
          class="text-muted small mb-0"
        >
          Ya puede volver a la aplicación.
        </p>

        <b-button
          v-else-if="successMessage"
          variant="primary"
          size="sm"
          class="no-print"
          :to="{ name: 'home' }"
        >
          Volver al inicio
        </b-button>

        <p
          v-else
          class="text-muted small mb-0"
        >
          Será redirigido al inicio en
          <strong>{{ redirectCountdown }}</strong>
          segundo<span v-if="redirectCountdown !== 1">s</span>...
        </p>

        <p
          v-if="errorMessage"
          class="text-muted small mb-0 mt-1"
        >
          Si el problema persiste, contáctanos al {{ business.phone }}
        </p>

        <hr class="my-2">

        <div class="legal-links">
          <b-link :to="{ name: 'legal-services' }">
            Servicios
          </b-link>
          <b-link :to="{ name: 'legal-terms' }">
            Términos
          </b-link>
          <b-link :to="{ name: 'legal-privacy' }">
            Privacidad
          </b-link>
          <b-link :to="{ name: 'legal-security' }">
            Seguridad
          </b-link>
          <b-link :to="{ name: 'legal-refunds' }">
            Devoluciones
          </b-link>
          <b-link :to="{ name: 'legal-delivery' }">
            Entrega
          </b-link>
          <b-link :to="{ name: 'legal-contact' }">
            Contacto
          </b-link>
        </div>

        <div class="business-info">
          <div>
            {{ business.name }} · {{ business.address }} ·
            {{ business.country }}
          </div>
          <div>
            <a :href="`tel:${business.phone}`">{{
              business.phone
            }}</a>
            ·
            <a :href="`mailto:${business.email}`">{{
              business.email
            }}</a>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import business from "@/business"
import checkoutService from "@/services/checkoutService"
import ability from "@/libs/acl/ability"
import { initialAbility } from "@/libs/acl/config"
import { clearGuestSession, isGuestSession } from "@/auth/guestSession"
import { BAvatar, BButton, BCard, BImg, BLink, BSpinner } from "bootstrap-vue"
import PaymentReceipt from "@/components/PaymentReceipt.vue"

export default {
  components: {
    PaymentReceipt,
    BButton,
    BCard,
    BAvatar,
    BSpinner,
    BImg,
    BLink,
  },
  data() {
    return {
      business,
      show: true,
      id: this.$route.params.id,
      status: this.$route.name.startsWith("checkout-approved")
        ? "approved"
        : "declined",
      isAnulate: this.$route.name.endsWith("-anulate"),
      queryData: {},
      // Se captura al montar: initRedirect cierra la sesión, y el template
      // necesita seguir sabiendo que este cierre fue de un invitado.
      wasGuest: isGuestSession(),
      errorMessage: null,
      successMessage: null,
      receipt: null,
      redirectCountdown: 3,
    }
  },
  computed: {
    messages() {
      return {
        approved: {
          success: "El pago se procesó correctamente.",
          error: "Hubo un error al procesar el pago.",
          reference: "Pago",
        },
        declined: {
          success: "El pago fue rechazado.",
          error: "Hubo un error al procesar el rechazo del pago.",
          reference: "Rechazo",
        },
      }[this.status]
    },
  },
  mounted() {
    this.getParams()
    this.processCheckout()
  },
  methods: {
    getParams() {
      this.queryData = this.$route.query
    },
    async processCheckout() {
      if (!this.id) {
        this.errorMessage = "ID de checkout no encontrado"
        this.show = false
        this.initRedirect()

        return
      }

      try {
        const data = await checkoutService.processCheckout(
          this.id,
          this.status,
          this.queryData,
          this.isAnulate,
        )

        // Viene en la misma respuesta del callback: el comprobante se pinta
        // sin una petición extra, que es el momento en que el cliente lo espera.
        this.receipt = data?.receipt || null
        this.successMessage = this.messages.success
      } catch (error) {
        this.errorMessage = this.messages.error
        console.error("Error:", error)
      } finally {
        this.show = false
        this.initRedirect()
      }
    },
    initRedirect() {
      /*
             * El invitado que vino desde una app externa NO se redirige.
             *
             * Este es el final del flujo: acaba de pagar y su sesión solo alcanza
             * para su pantalla de pago. Mandarlo a home lo dejaba en "no autorizado"
             * — el último mensaje que veía después de pagarnos. Se queda en el
             * comprobante y cierra la ventana cuando quiera; si viene dentro de un
             * WebView, la app decide cuándo cerrarla.
             */
      if (this.wasGuest) {
        this.redirectCountdown = 0

        /*
                 * La sesión de invitado termina acá: se emitió para pagar un contrato
                 * y el pago ya ocurrió. Dejarla viva convertía esta pantalla en un
                 * encierro — el router devolvía a /pagar/:id desde cualquier ruta, y
                 * la única salida vivía en "no autorizado", a donde ya no se llegaba.
                 *
                 * Cerrarla acá deja el navegador limpio: el portal vuelve a funcionar
                 * normal para quien entre después en ese mismo dispositivo.
                 */
        clearGuestSession()
        ability.update(initialAbility)

        return
      }

      /*
             * Un pago aprobado no se redirige solo.
             *
             * Es la única pantalla que el cliente puede querer imprimir, capturar o
             * simplemente releer, y tres segundos no alcanzan ni para llegar al
             * botón. Se queda hasta que decida salir. La cuenta regresiva se
             * conserva solo cuando no hay nada que leer: pago rechazado o error.
             */
      if (this.successMessage) {
        this.redirectCountdown = 0

        return
      }

      const countdownInterval = setInterval(() => {
        if (this.redirectCountdown > 0) {
          // eslint-disable-next-line no-plusplus
          this.redirectCountdown--
        } else {
          clearInterval(countdownInterval)
          this.$router.push({ name: "home" })
        }
      }, 1000)
    },
  },
}
</script>

<style scoped>
.receipt-note {
    margin-top: 0.75rem;
    margin-bottom: 0;
    font-size: 0.75rem;
    text-align: center;
    color: #6e6b7b;
}

.checkout-result {
    min-height: 80vh;
    padding: 1rem;
}
.checkout-result-card {
    max-width: 420px;
    width: 100%;
}

.legal-links {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.25rem 0.75rem;
    font-size: 0.75rem;
}

.legal-links a {
    color: var(--tm-text-secondary);
    text-decoration: none;
}

.legal-links a:hover {
    text-decoration: underline;
}

.business-info {
    margin-top: 0.75rem;
    font-size: 0.7rem;
    color: var(--tm-text-muted);
    line-height: 1.5;
}

.business-info a {
    color: var(--tm-text-muted);
}
</style>
